import * as THREE from "three";
import type { BaseState } from "../base/api.js";
import { fieldSurface } from "./fieldSurface.js";
import { settlementRoads } from "./settlement.js";
import { settlementWorkyards, type YardModel } from "./workyards.js";
import { InstancedModel } from "../base/sceneParts.js";

import { addGrove, disposeGroves, type TreeSite } from "./nativeGrove.js";
export { addGrove } from "./nativeGrove.js";

function heightAt(x: number, z: number): number {
  const distance = Math.max(Math.abs(x), Math.abs(z));
  return (
    -0.12 +
    Math.max(0, distance - 17) * 0.035 +
    Math.sin(x * 0.13) * Math.cos(z * 0.12) * Math.min(2, Math.max(0, distance - 15) * 0.09)
  );
}

export function buildValley(): THREE.Group {
  const group = new THREE.Group();
  const terrain = new THREE.PlaneGeometry(180, 180, 70, 70);
  terrain.rotateX(-Math.PI / 2);
  const positions = terrain.getAttribute("position");
  const colors: number[] = [];
  const color = new THREE.Color();
  for (let i = 0; i < positions.count; i++) {
    const x = positions.getX(i),
      z = positions.getZ(i);
    positions.setY(i, heightAt(x, z));
    const mix = (Math.sin(x * 0.19 + z * 0.08) + Math.cos(z * 0.23)) * 0.14 + 0.5;
    color.set(0x575d3c).lerp(new THREE.Color(0x8c8158), mix);
    colors.push(color.r, color.g, color.b);
  }
  terrain.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  terrain.computeVertexNormals();
  const land = new THREE.Mesh(terrain, fieldSurface(0xffffff));
  land.receiveShadow = true;
  group.add(land);
  const sites: TreeSite[] = [];
  for (let i = 0; i < 145; i++) {
    const angle = i * 2.39996,
      radius = 20 + Math.sqrt(i / 145) * 44;
    const x = Math.cos(angle) * radius,
      z = Math.sin(angle) * radius;
    if (z > 8 && Math.abs(x - 4) < 6) continue;
    sites.push({ x, z, y: heightAt(x, z), scale: 0.8 + (i % 7) * 0.19, autumn: i % 9 === 0 });
  }
  addGrove(group, sites);
  const rock = new THREE.InstancedMesh(
    new THREE.DodecahedronGeometry(1, 0),
    new THREE.MeshStandardMaterial({ color: 0xa4a28a, roughness: 1, flatShading: true }),
    42,
  );
  const dummy = new THREE.Object3D();
  for (let i = 0; i < 42; i++) {
    const angle = i * 2.39996,
      radius = 18 + (i % 11) * 3;
    const x = Math.cos(angle) * radius,
      z = Math.sin(angle) * radius;
    dummy.position.set(x, heightAt(x, z) - 0.25, z);
    dummy.scale.set(0.5 + (i % 3) * 0.35, 0.45 + (i % 4) * 0.2, 0.8);
    dummy.rotation.set(i, i * 1.3, i * 0.4);
    dummy.updateMatrix();
    rock.setMatrixAt(i, dummy.matrix);
  }
  rock.castShadow = rock.receiveShadow = true;
  group.add(rock);
  return group;
}

/** Wild vegetation follows authoritative footprints and clears when a building moves onto it. */
export class ColonyLandscape {
  readonly group = new THREE.Group();
  readonly gardenCells = new Set<string>();
  private signature = "";
  private readonly workyardModels = new Map<YardModel, InstancedModel>();
  update(state: BaseState): void {
    const signature = `${state.grid.size}|${state.buildings.map((b) => `${b.id}:${b.x},${b.y},${b.w},${b.h},${b.rot}`).join(";")}|${state.zones.map((z) => `${z.id}:${z.state}`).join(";")}|${state.loop?.decorations.map((d) => `${d.x},${d.y}`).join(";") ?? ""}`;
    if (signature === this.signature) return;
    this.signature = signature;
    this.clear();
    const occupied = new Set<string>();
    for (const garden of state.loop?.decorations ?? [])
      for (let y = garden.y - 1; y <= garden.y + 1; y++) for (let x = garden.x - 1; x <= garden.x + 1; x++) occupied.add(`${x},${y}`);
    for (const b of state.buildings)
      for (let y = b.y - 1; y < b.y + b.h + 1; y++)
        for (let x = b.x - 1; x < b.x + b.w + 1; x++) occupied.add(`${x},${y}`);
    const half = state.grid.size / 2;
    const roads = settlementRoads(state);
    const yards = settlementWorkyards(state, roads);
    for (const cell of roads) occupied.add(cell);
    this.addGround(state, new Set([...yards.paving, ...roads]));
    this.gardenCells.clear();
    for (const site of yards.sites) this.gardenCells.add(`${site.x},${site.y}`);
    const sites: TreeSite[] = [];
    const randomAt = (x: number, y: number, salt: number) => {
      const value = Math.sin(x * 127.1 + y * 311.7 + salt * 74.7) * 43758.5453;
      return value - Math.floor(value);
    };
    for (let y = 1; y < state.grid.size - 1; y++)
      for (let x = 1; x < state.grid.size - 1; x++) {
        if (occupied.has(`${x},${y}`)) continue;
        if ([...roads].some(cell => { const [rx, ry] = cell.split(",").map(Number); return Math.abs(rx! - x) <= 1 && Math.abs(ry! - y) <= 1; })) continue;
        const zone = state.zones.find(
          (z) => x >= z.rect.x && x < z.rect.x + z.rect.w && y >= z.rect.y && y < z.rect.y + z.rect.h,
        );
        const wild = zone !== undefined && zone.state !== "open";
        const patch = .5 + .25 * Math.sin(x * .63 + y * .31) + .25 * Math.cos(y * .59 - x * .17);
        if (randomAt(x, y, 1) > (wild ? .14 + patch * .35 : .035 + patch * .08)) continue;
        const wx = x - half + .5 + (randomAt(x, y, 2) - .5) * .55;
        const wz = y - half + .5 + (randomAt(x, y, 3) - .5) * .55;
        if (sites.some(site => Math.hypot(site.x - wx, site.z - wz) < 1.65)) continue;
        const scale = (wild ? .52 : .38) + randomAt(x, y, 4) * .23;
        const clearance = scale * 1.2 + .3;
        for (let yy = y - 2; yy <= y + 2; yy++)
          for (let xx = x - 2; xx <= x + 2; xx++) {
            if (Math.abs(xx - x) < clearance && Math.abs(yy - y) < clearance)
              this.gardenCells.add(`${xx},${yy}`);
          }
        sites.push({
          x: wx,
          z: wz,
          y: 0.04,
          scale,
          autumn: randomAt(x, y, 5) > .93,
        });
      }
    addGrove(this.group, sites);
    this.addServiceRoads(state, roads);
    const matrix = new THREE.Matrix4(), up = new THREE.Vector3(0, 1, 0);
    for (const id of ["prop.yard_supply", "prop.yard_service", "prop.yard_water"] as const) {
      let model = this.workyardModels.get(id);
      if (!model) { model = new InstancedModel(id); this.workyardModels.set(id, model); }
      const transforms = yards.sites.filter(site => site.model === id).map(site => matrix.compose(
        new THREE.Vector3(site.x - half + .5, .016, site.y - half + .5),
        new THREE.Quaternion().setFromAxisAngle(up, site.rotation), new THREE.Vector3(.86, .86, .86),
      ).clone());
      model.setTransforms(transforms); this.group.add(model.group);
    }
  }
  /** Unused ground stays rough; paving remains around actual workplaces and under purchased skins. */
  private addGround(state: BaseState, occupied: Set<string>): void {
    const cells: [number, number][] = [];
    for (let y = 0; y < state.grid.size; y++) for (let x = 0; x < state.grid.size; x++)
      if (!occupied.has(`${x},${y}`)) cells.push([x, y]);
    if (!cells.length) return;
    const geometry = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    const material = fieldSurface(0x887b60, "yard");
    const ground = new THREE.InstancedMesh(geometry, material, cells.length);
    ground.name = "colony.rough-ground";
    const dummy = new THREE.Object3D();
    cells.forEach(([x, y], i) => { dummy.position.set(x - state.grid.size / 2 + .5, .008, y - state.grid.size / 2 + .5); dummy.updateMatrix(); ground.setMatrixAt(i, dummy.matrix); });
    ground.receiveShadow = true;
    this.group.add(ground);
  }
  private addServiceRoads(state: BaseState, network: Set<string>): void {
    const size = state.grid.size, half = size / 2;
    const roads = [...network].map(cell => cell.split(",").map(Number) as [number, number]);
    if (!roads.length) return;
    const surface = new THREE.InstancedMesh(new THREE.BoxGeometry(1,.026,1), fieldSurface(0x545750, true), roads.length);
    const straight = roads.filter(([x, y]) => {
      const n = network.has(`${x},${y - 1}`), s = network.has(`${x},${y + 1}`), w = network.has(`${x - 1},${y}`), e = network.has(`${x + 1},${y}`);
      return (n && s && !w && !e) || (w && e && !n && !s);
    });
    const markings = new THREE.InstancedMesh(new THREE.BoxGeometry(.038,.01,.35), new THREE.MeshStandardMaterial({ color: 0xbea66f, roughness: .93 }), straight.length);
    surface.name = "colony.service-roads";
    const dummy = new THREE.Object3D();
    roads.forEach(([x,y],i) => {
      dummy.position.set(x-half+.5,.019,y-half+.5); dummy.rotation.set(0,0,0); dummy.updateMatrix(); surface.setMatrixAt(i,dummy.matrix);
    });
    straight.forEach(([x, y], i) => { dummy.position.set(x-half+.5,.038,y-half+.5); dummy.rotation.y=network.has(`${x},${y + 1}`)?0:Math.PI/2; dummy.updateMatrix(); markings.setMatrixAt(i,dummy.matrix); });
    surface.receiveShadow = true;
    this.group.add(surface,markings);
  }
  private clear(): void {
    disposeGroves(this.group);
    // Model templates own shared geometry/materials. Detach before releasing this layout's own meshes.
    for (const model of this.workyardModels.values()) model.group.removeFromParent();
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.geometry.dispose();
        (Array.isArray(o.material) ? o.material : [o.material]).forEach((m) => m.dispose());
        if (o instanceof THREE.InstancedMesh) o.dispose();
      }
    });
    this.group.clear();
  }
  dispose(): void {
    this.clear();
    for (const model of this.workyardModels.values()) model.dispose();
    this.workyardModels.clear();
  }
}
